import type {
  FileAsset,
  GetAttachmentsResponseType,
  LinkAsset,
} from "@nlc/shared-types";

/**
 * Filters attachments by category and link type
 */
export function filterAttachments(
  attachments: GetAttachmentsResponseType,
  category: "asset" | "example",
  isLink: boolean,
): GetAttachmentsResponseType {
  return attachments.filter(
    (attachment) =>
      attachment.isLink === isLink && attachment.category === category,
  );
}

/**
 * Maps link attachments to LinkAsset format
 */
export function mapToLinkAssets(
  attachments: GetAttachmentsResponseType,
): LinkAsset[] {
  return attachments.map((attachment) => ({
    attachmentId: attachment.id,
    category: attachment.category as "asset" | "example",
    createdAt: attachment.createdAt
      ? new Date(attachment.createdAt).toISOString()
      : null,
    id: String(attachment.id),
    url: attachment.url || "",
  }));
}

/**
 * Maps file attachments to FileAsset format
 */
export function mapToFileAssets(
  attachments: GetAttachmentsResponseType,
): FileAsset[] {
  return attachments.map((attachment) => ({
    attachmentId: attachment.id,
    awsDownloadUrl: attachment.url,
    createdAt: attachment.createdAt
      ? new Date(attachment.createdAt).toISOString()
      : null,
    file: new File([], attachment.name),
    id: String(attachment.id),
    mime: attachment.mime,
    status: "success" as const,
  }));
}

/**
 * Maps attachments to assets and examples
 */
export function mapAttachmentsToAssets(
  attachments: GetAttachmentsResponseType,
): {
  assets: Array<FileAsset | LinkAsset>;
  example: Array<FileAsset | LinkAsset>;
} {
  const assetLinks = mapToLinkAssets(
    filterAttachments(attachments, "asset", true),
  );
  const exampleLinks = mapToLinkAssets(
    filterAttachments(attachments, "example", true),
  );
  const assetFiles = mapToFileAssets(
    filterAttachments(attachments, "asset", false),
  );
  const exampleFiles = mapToFileAssets(
    filterAttachments(attachments, "example", false),
  );

  return {
    assets: [...assetFiles, ...assetLinks],
    example: [...exampleFiles, ...exampleLinks],
  };
}
